import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { organizationsApi, programsApi } from 'api';
import { useOrganizationPanel } from 'contexts/organizationPanelContext';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Program } from 'types/Program';
import { Dialog } from 'components/_new/Dialog';
import { InputText } from 'components/_new/InputText';
import { Button } from 'components/_new/Button';

type CopyProgramFormData = {
	sourceProgramId: string;
	displayName: string;
	name: string;
	landingPageContent: boolean;
	contractSettings: boolean;
	recruitmentForms: boolean;
	knowledgeBaseMaterials: boolean;
};

interface CopyProgramDialogProps {
	visible: boolean;
	onHide: () => void;
	onSuccess?: () => void;
}

const slugify = (text: string, organizationName: string): string => {
	const slug = text
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/(^-|-$)/g, '');
	return `${organizationName}/${slug}`;
};

export const CopyProgramDialog: React.FC<CopyProgramDialogProps> = ({ visible, onHide, onSuccess }) => {
	const { t } = useTranslation();
	const toast = useRef<Toast>(null);
	const {
		currentOrganization: { organizationName },
	} = useOrganizationPanel();

	// Fetch programs for the dropdown
	const { data: programs = [] } = useQuery(
		['getOrganizationPrograms', organizationName],
		() => organizationsApi.getOrganizationPrograms({ organizationName: organizationName! }),
		{
			enabled: Boolean(organizationName) && visible,
		}
	);

	const form = useForm<CopyProgramFormData>({
		defaultValues: {
			sourceProgramId: '',
			displayName: '',
			name: '',
			landingPageContent: true,
			contractSettings: true,
			recruitmentForms: true,
			knowledgeBaseMaterials: true,
		},
		mode: 'onChange',
	});

	// Auto-generate slug from display name
	useEffect(() => {
		if (form.watch('displayName') && organizationName) {
			const timeoutId = setTimeout(() => {
				form.setValue('name', slugify(form.watch('displayName'), organizationName));
			}, 300);
			return () => clearTimeout(timeoutId);
		}
		return undefined;
	}, [form.watch('displayName'), form.setValue, organizationName]);

	// Reset form when dialog opens/closes
	useEffect(() => {
		if (visible) {
			form.reset({
				sourceProgramId: '',
				displayName: '',
				name: '',
				landingPageContent: true,
				contractSettings: true,
				recruitmentForms: true,
				knowledgeBaseMaterials: true,
			});
		}
	}, [visible, form]);

	const copyProgram = useMutation<Program, Error, CopyProgramFormData>(
		async (data) => {
			return programsApi.copyProgram({
				organizationName: organizationName!,
				sourceProgramId: data.sourceProgramId,
				displayName: data.displayName,
				name: data.name,
				copyOptions: {
					landingPageContent: data.landingPageContent,
					contractSettings: data.contractSettings,
					recruitmentForms: data.recruitmentForms,
					knowledgeBaseMaterials: data.knowledgeBaseMaterials,
				},
			});
		},
		{
			onSuccess: () => {
				toast.current?.show({
					severity: 'success',
					summary: t('organizationPanel.copyProgram.success'),
					life: 3000,
				});
				onSuccess?.();
				onHide();
			},
			onError: (error: any) => {
				const message = error.response?.data?.message;
				const errorKey = message === 'Program name already exists' ? 'nameExists' : 'default';

				toast.current?.show({
					severity: 'error',
					summary: t(`organizationPanel.copyProgram.error.${errorKey}`),
					life: 3000,
				});
			},
		}
	);

	const handleConfirm = form.handleSubmit((data: CopyProgramFormData) => {
		if (!/^[a-z0-9-/]+$/.test(data.name)) {
			toast.current?.show({
				severity: 'error',
				summary: t('organizationPanel.program.error.invalidCharacters'),
				life: 3000,
			});
			return;
		}
		copyProgram.mutate(data);
	});

	const programOptions = programs.map((program: Program) => ({
		label: program.displayName,
		value: program.id,
	}));

	return (
		<>
			<Toast ref={toast} />
			<Dialog visible={visible} onHide={onHide} title={t('organizationPanel.copyProgram.title')} size="md">
				<form onSubmit={handleConfirm}>
					<p>{t('organizationPanel.copyProgram.description')}</p>

					<div className="flex flex-column gap-3">
						<div className="flex flex-row align-items-center">
							<label className="w-3">{t('organizationPanel.copyProgram.sourceProgram')}</label>
							<Controller
								control={form.control}
								name="sourceProgramId"
								rules={{ required: true }}
								render={({ field }) => (
									<Dropdown
										{...field}
										options={programOptions}
										placeholder={t('organizationPanel.copyProgram.selectProgram')}
										className="w-full"
										disabled={copyProgram.isLoading}
									/>
								)}
							/>
						</div>

						<div className="flex flex-row align-items-center">
							<label className="w-3">{t('organizationPanel.copyProgram.newProgramName')}</label>
							<Controller
								control={form.control}
								name="displayName"
								rules={{ required: true }}
								render={({ field }) => (
									<InputText
										{...field}
										maxLength={60}
										disabled={copyProgram.isLoading}
										placeholder={t('organizationPanel.copyProgram.enterProgramName')}
									/>
								)}
							/>
						</div>

						<div className="flex flex-row align-items-center">
							<label className="w-3">{t('organizationPanel.copyProgram.programPage')}</label>
							<Controller
								control={form.control}
								name="name"
								render={({ field }) => <InputText {...field} disabled={copyProgram.isLoading} />}
							/>
						</div>

						<div className="flex flex-column gap-2 mt-3">
							<label className="font-semibold">
								{t('organizationPanel.copyProgram.selectItemsToCopy')}
							</label>

							<div className="flex flex-column gap-2 ml-3">
								<Controller
									control={form.control}
									name="landingPageContent"
									render={({ field }) => (
										<div className="flex align-items-center">
											<Checkbox
												{...field}
												checked={field.value}
												disabled={copyProgram.isLoading}
											/>
											<label className="ml-2">
												{t('organizationPanel.copyProgram.landingPageContent')}
											</label>
										</div>
									)}
								/>

								<Controller
									control={form.control}
									name="contractSettings"
									render={({ field }) => (
										<div className="flex align-items-center">
											<Checkbox
												{...field}
												checked={field.value}
												disabled={copyProgram.isLoading}
											/>
											<label className="ml-2">
												{t('organizationPanel.copyProgram.contractSettings')}
											</label>
										</div>
									)}
								/>

								<Controller
									control={form.control}
									name="recruitmentForms"
									render={({ field }) => (
										<div className="flex align-items-center">
											<Checkbox
												{...field}
												checked={field.value}
												disabled={copyProgram.isLoading}
											/>
											<label className="ml-2">
												{t('organizationPanel.copyProgram.recruitmentForms')}
											</label>
										</div>
									)}
								/>

								{/* <Controller
									control={form.control}
									name="knowledgeBaseMaterials"
									render={({ field }) => (
										<div className="flex align-items-center">
											<Checkbox {...field} checked={field.value} disabled />
											<label className="ml-2">
												{t('organizationPanel.copyProgram.knowledgeBaseMaterials')}
											</label>
										</div>
									)}
								/> */}
							</div>
						</div>
					</div>

					<div className="flex justify-content-end gap-2 mt-4">
						<Button label={t('actions.cancel')} onClick={onHide} variant="primary-outlined" />
						<Button
							label={t('actions.confirm')}
							submit
							loading={copyProgram.isLoading}
							disabled={!form.watch('sourceProgramId') || !form.watch('displayName')}
						/>
					</div>
				</form>
			</Dialog>
		</>
	);
};
